import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {AbstractValueAccessorComponent} from '../component/value-accessor.component';
import {Observable, of, Subject} from 'rxjs';
import {
  checkValidity,
  dispatchChangeEvent,
  extractValue,
  filterByTerm,
  lodash,
  newObjectFromValue,
  uuid
} from '../utils';
import Modal from 'bootstrap/js/dist/modal';
import {AppContext} from "../../app-context";
import {TypeaheadDirective} from "../typeahead/typeahead.directive";
import {map} from "rxjs/operators";

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchComponent), multi: true }
    ],
    host: { 'class': 'input-group' },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchComponent extends AbstractValueAccessorComponent<any> implements OnInit {
  appContext = AppContext;

  inputModel;
  selectedItem;
  options?: any[];
  searching: boolean = false;

  @ViewChild('newValueModal') newValueModal: ElementRef;
  private changeDetectorRef = inject(ChangeDetectorRef);

  private outputSubject = new Subject<any[]>();
  private outputObservable = this.outputSubject.asObservable();
  private isInitialized = false;
  filterTerm: string = "";

  @Input() searchFunction: (arg: string) => Observable<any[]>;
  @Input() styling: ((arg: any) => any) = null;
  @Input() dataKey: string;
  @Input() inputFormatter : string | ((value : any) => string);
  @Input() resultFormatter : string | ((value : any) => string);
  @Input() placeholder = "";
  @Input() placement = "bottom-left";
  @Input() disabled: boolean;
  @Input() minCharacters: number = 1;
  @Input() required;
  @Input() id = uuid();
  @Input() optionsProvider: Observable<any[]>;
  @Input() cssClass: string;
  @Input() valueType : string = "entry";
  @Input() newValueFunction : ((input : null | string) => any) = (() => { return {} });
  @Input() newValueTemplate : TemplateRef<any>;
  @Input() newValueModalClass : string;

  @Input() alwaysShowNewValue: boolean;
  @Input() seachFormattedOptions: boolean = false;
  @Input() newValueFromInputText: string;
  @Input() newValueFromInputFunction : string | ((input : string) => any);
  @Input() showDelete : boolean = false;
  @Input() deleteFunction : (arg: {details: string}) => void;
  @Input() emptyOnBlur : boolean = true;
  // @Input() allowDeletion: boolean = false;
  @Input({ alias: 'options' })
  set searchOptions(options: any[]) {
    if (this.options !== options) {
      this.options = options;
      if (this.isInitialized) {
        this.refreshCurrentValue();
      }
    }
  }

  _refreshWatch;
  @Input()
  set refreshWatch(value) {
    const changed = !lodash.isEqual(this._refreshWatch, value);
    this._refreshWatch = value === undefined ? null : value;
    setTimeout(() => {
      if (this.searchFunction && changed) {
        this.search(false);
      }
    }, 0);
  }

  @ViewChild(TypeaheadDirective) inputElementTypeAhead: TypeaheadDirective;
  @Input() resultTemplate: TemplateRef<any>;

  constructor(private elementRef: ElementRef) {
    super();
  }

  get isSelect(): boolean {
    return !!this.options || !!this.optionsProvider;
  }

  onInput = (text$: Observable<string>) => {
    text$.subscribe(term => {
      this.filterTerm = term;
      this.search();
    });
    return this.outputObservable;
  };

  clear = (ev: any) => {
    if (ev.target.value === '') {
      this.onSelect(null);
    }
  }

  search = (outputResults: boolean = true) => {
    if (!this.searching) {
      let noValues = [];
      if (this.newValueTemplate) {
        if (this.selectedItem) {
          const editValue = lodash.assign({}, this.selectedItem);
          editValue.requiresEditing = true;
          editValue.previousValue = this.selectedItem;
          noValues.push(editValue);
        } else {
          const newValue = this.newValueFunction(this.filterTerm);
          if (newValue) {
            newValue.requiresEditing = true;
            noValues.push(newValue);
          }
        }
      }
      if ((!this.filterTerm && this.minCharacters > 0) || this.filterTerm?.length < this.minCharacters) {
        this.outputSubject.next(noValues);
        return;
      }
      if (!this.emptyOnBlur && this.filterTerm != "") {
        this.inputModel = {details:this.filterTerm};
        this.selectedItem = {details:this.filterTerm};
        this.onModelChange();
      }
      this.searching = true;
      const lastTerm = this.filterTerm;
      this.searchFunction(lastTerm).subscribe(results => {
        results = noValues.concat(results);
        if ((results.length === 0 || this.alwaysShowNewValue) && this.newValueFromInputFunction) {
          const newValue = (<any>this.newValueFromInputFunction)(this.filterTerm);
          if (this.newValueFromInputText) {
            newValue["$isNewValueRecord"] = () => `+ ${this.newValueFromInputText}`;
            newValue['cssClass'] = 'border-top border-light';
          }
          if (newValue) {
            results.push(newValue);
          }
        }
        if (outputResults) {
          this.outputSubject.next(results);
        }
        if (this.isSelect && this.required && results?.length === 1) {
          this.onSelect(results[0]);
        }
        this.searching = false;
        if (lastTerm !== this.filterTerm) {
          this.search(outputResults);
        }
      })
    }
  };

  ngOnInit() {
    if (this.options && this.seachFormattedOptions && typeof this.inputFormatter !== 'string') {
      const searchFormatter = this.inputFormatter as ((value : any) => string);
      this.searchFunction = (term: string) => of(
        this.options.map(option => {
          return {
            value: option,
            formattedValue: searchFormatter(option)
          }
        }).filter(
          wrappedOption => filterByTerm(term)(wrappedOption.formattedValue)
        ).map(
          wrappedOption => wrappedOption.value
        )
      );
      this.minCharacters = 0;
    } else if (this.options) {
      this.searchFunction = (term: string) => of(this.options.filter(filterByTerm(term)));
      this.minCharacters = 0;
    } else if (this.optionsProvider) {
      this.searchFunction = (term: string) => this.optionsProvider.pipe(map(o => o.filter(filterByTerm(term))));
      this.minCharacters = 0;
    }
    if (!this.searchFunction) {
      throw new Error('Attribute searchFunction is required for app-search component');
    }
    if (this.required === "") {
      this.required = true;
    }
    if (!this.inputFormatter) {
      this.inputFormatter = value => this.dataKey ? extractValue(value, this.dataKey) : value;
    }
    if (typeof this.inputFormatter === 'string') {
      const field : string = this.inputFormatter;
      this.inputFormatter = v => extractValue(v, field);
    }
    if (!this.resultFormatter) {
      this.resultFormatter = this.inputFormatter;
    }
    if (typeof this.resultFormatter === 'string') {
      const field : string = this.resultFormatter;
      this.resultFormatter = v => extractValue(v, field);
    }
    if (typeof this.newValueFromInputFunction === 'string') {
      const objectFields : string = this.newValueFromInputFunction;
      this.newValueFromInputFunction = v => newObjectFromValue(v, objectFields);
    }
    if (this.newValueTemplate) {
      const resultFormatter = this.resultFormatter;
      const inputFormatter = this.inputFormatter;
      this.resultFormatter = v =>
        v.requiresEditing ? (this.selectedItem && this.inputModel ? 'Edit ' : 'Create new ') + this.valueType: resultFormatter(v);
      this.inputFormatter = v => v.requiresEditing && !v.previousValue ? '' : inputFormatter(v);
    }
    this.isInitialized = true;
  }

  refreshCurrentValue(): void {
    this.inputElementTypeAhead.writeValue(this.value);
  }

  onFocus = () => {
    if (this.minCharacters === 0 || this.newValueTemplate) {
      this.search();
    }
  }

  onBlur = () => {
    // Angular executes onBlur first before setting model through onSelect, wait for onSelect and its setTimeout to trigger first
    setTimeout(() => {
      if ((this.filterTerm && this.filterTerm != this.selectedItem) || this.inputModel !== this.selectedItem) {
        if (!this.emptyOnBlur) {
          this.onSelect({details:this.filterTerm})
        }
        else {
          if (this.inputModel === null) {
            // Vieze hack om angular te dwingen het veld leeg te maken
            this.onSelect(undefined);
            this.changeDetectorRef.detectChanges();
          }
          this.onSelect(null);
        }
      }
      if (this.inputElementTypeAhead) {
        this.inputElementTypeAhead.closeWindow();
      }
    }, 300);
  };

  onSelect = (value) => {
    this.inputModel = value;
    this.selectedItem = value;
    this.searching = false;
    this.filterTerm = "";
    if (!this.selectedItem || !this.selectedItem.requiresEditing) {
      setTimeout(() => {
        this.onModelChange();
        dispatchChangeEvent(this.elementRef.nativeElement);
      }, 0);
    }
    this.toggleModal();
  };

  onDelete = (value) => {
    if (value != null) {
      this.deleteFunction(value);
    }
    setTimeout(() => {
      this.search();
    }, 200);
  };

  get value(): any {
    return this.selectedItem;
  }

  writeValue(value: any): void {
    this.selectedItem = value === undefined ? null : value;
    this.inputModel = this.selectedItem;
    this.toggleModal();
  }

  onCompleteEditing() {
    if (checkValidity(this.newValueModal)) {
      setTimeout(() => {
        delete this.selectedItem.requiresEditing;
        delete this.selectedItem.previousValue;
        const update = lodash.assign({}, this.selectedItem);
        this.onSelect(update);
      }, 0);
      return true;
    }
    return false;
  }

  onCancelEditing() {
    delete this.selectedItem.requiresEditing;
    const previousValue = this.selectedItem.previousValue;
    if (previousValue) {
      delete this.selectedItem.previousValue;
    }
    this.onSelect(previousValue);
  }

  private toggleModal() {
    if (this.newValueTemplate && this.selectedItem && this.selectedItem.requiresEditing) {
      setTimeout(() => {
        if (this.newValueModal) {
          const modal = new Modal(this.newValueModal.nativeElement, {
            backdrop: "static",
            keyboard: false,
            focus: true}
          );
          this.newValueModal.nativeElement.classList.add('fade');
          modal.show();
        }
      }, 0);
    } else if (this.newValueModal) {
      this.newValueModal.nativeElement.classList.remove('fade');
      const modal = $(this.newValueModal.nativeElement);
      modal.modal('hide');
    }
  }
}
